import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi, errorApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { entityFeedbackApiRef } from '@backstage-community/plugin-entity-feedback';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { ReactNode, useEffect, useRef, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

interface EntityFeedbackResponse {
  id: string;
  label: string;
}

export interface CustomFeedbackResponseDialogProps {
  entity: Entity;
  feedbackDialogResponses?: EntityFeedbackResponse[];
  feedbackDialogTitle?: ReactNode;
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  qetaPostId?: string;
}

const defaultFeedbackResponses: EntityFeedbackResponse[] = [
  { id: 'incorrect', label: 'Incorrect info' },
  { id: 'missing', label: 'Missing info' },
  { id: 'other', label: 'Other' },
];

const useStyles = makeStyles(
  theme => ({
    contactConsent: {
      marginTop: theme.spacing(1.5),
    },
    commentBoxes: {
      marginBottom: theme.spacing(1.5),
    },
    boxContainer: {
      marginBottom: theme.spacing(1.5),
      marginTop: theme.spacing(1.5),
      marginLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    formLabel: {
      marginBottom: theme.spacing(1.5),
    },
    dialogActions: {
      justifyContent: 'flex-start',
    },
  }),
  { name: 'CustomEntityFeedbackDialog' },
);

export const CustomFeedbackResponseDialog = (
  props: CustomFeedbackResponseDialogProps,
) => {
  const {
    entity,
    feedbackDialogResponses = defaultFeedbackResponses,
    feedbackDialogTitle = 'Tell us what could be better',
    open,
    onClose,
    onSubmitted,
    qetaPostId,
  } = props;
  const classes = useStyles();
  const errorApi = useApi(errorApiRef);
  const feedbackApi = useApi(entityFeedbackApiRef);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(true);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const identityApi = useApi(identityApiRef);

  const [responseSelections, setResponseSelections] = useState<
    Record<string, boolean>
  >(Object.fromEntries(feedbackDialogResponses.map(r => [r.id, false])));
  const [comments, setComments] = useState<{
    responseComments: Record<string, string>;
    additionalComments: string;
  }>({
    responseComments: {},
    additionalComments: '',
  });
  const [consent, setConsent] = useState(true);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [prevQetaPostIds, setPrevQetaPostIds] = useState<string[]>([]);

  // Clear the close timer on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Reset dialog visibility when re-opened
  useEffect(() => {
    if (open) setIsDialogVisible(true);
  }, [open]);

  // Fetch previous response when dialog opens and pre-fill
  useEffect(() => {
    if (!open || !entity) return () => { };

    let cancelled = false;
    const fetchPrevious = async () => {
      setLoadingPrev(true);
      try {
        const identity = await identityApi.getBackstageIdentity();
        const responses = await feedbackApi.getResponses(
          stringifyEntityRef(entity),
        );
        const myResponse = responses.find(
          r => r.userRef === identity.userEntityRef,
        );
        if (myResponse && !cancelled) {
          // Pre-fill response selections
          const selections: Record<string, boolean> = Object.fromEntries(
            feedbackDialogResponses.map(r => [r.id, false]),
          );
          if (myResponse.response) {
            myResponse.response.split(',').forEach(id => {
              const trimmed = id.trim();
              if (trimmed in selections) {
                selections[trimmed] = true;
              }
            });
          }
          setResponseSelections(selections);

          // Pre-fill comments and extract existing Q&A post IDs
          let parsedComments: {
            responseComments: Record<string, string>;
            additionalComments: string;
          } = { responseComments: {}, additionalComments: '' };
          if (myResponse.comments) {
            try {
              const parsed = JSON.parse(myResponse.comments);
              parsedComments = {
                responseComments: parsed.responseComments || {},
                additionalComments: parsed.additionalComments || '',
              };
              const existingIds: string[] = Array.isArray(parsed.qetaPostIds)
                ? parsed.qetaPostIds
                : parsed.qetaPostId
                  ? [parsed.qetaPostId]
                  : [];
              setPrevQetaPostIds(existingIds);
            } catch {
              parsedComments = {
                responseComments: {},
                additionalComments: myResponse.comments,
              };
              setPrevQetaPostIds([]);
            }
          } else {
            setPrevQetaPostIds([]);
          }
          setComments(parsedComments);

          // Pre-fill consent
          if (myResponse.consent !== undefined) {
            setConsent(myResponse.consent);
          }
        } else if (!cancelled) {
          // No previous response, reset to defaults
          setResponseSelections(
            Object.fromEntries(
              feedbackDialogResponses.map(r => [r.id, false]),
            ),
          );
          setComments({ responseComments: {}, additionalComments: '' });
          setConsent(true);
          setPrevQetaPostIds([]);
        }
      } catch {
        // Non-critical: reset to blank form
        if (!cancelled) {
          setResponseSelections(
            Object.fromEntries(
              feedbackDialogResponses.map(r => [r.id, false]),
            ),
          );
          setComments({ responseComments: {}, additionalComments: '' });
          setConsent(true);
          setPrevQetaPostIds([]);
        }
      } finally {
        if (!cancelled) setLoadingPrev(false);
      }
    };
    fetchPrevious();
    return () => {
      cancelled = true;
    };
  }, [open, entity, feedbackApi, identityApi, feedbackDialogResponses]);

  const [{ loading: saving }, saveResponse] = useAsyncFn(async () => {
    const filteredResponseComments = Object.entries(
      comments.responseComments,
    ).reduce(
      (entry, [key, value]) => {
        if (responseSelections[key]) {
          entry[key] = value;
        }
        return entry;
      },
      {} as Record<string, string>,
    );
    const mergedPostIds = Array.from(new Set([
      ...prevQetaPostIds,
      ...(qetaPostId ? [qetaPostId] : []),
    ]));
    const filteredComments = {
      ...comments,
      responseComments: filteredResponseComments,
      ...(mergedPostIds.length > 0 ? { qetaPostIds: mergedPostIds } : {}),
    };
    try {
      await feedbackApi.recordResponse(stringifyEntityRef(entity), {
        comments: JSON.stringify(filteredComments),
        consent,
        response: Object.keys(responseSelections)
          .filter(id => responseSelections[id])
          .join(','),
      });
      onSubmitted?.();
      setIsDialogVisible(false);
      setSuccessOpen(true);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(onClose, 2000);
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [
    comments,
    consent,
    entity,
    feedbackApi,
    errorApi,
    onClose,
    onSubmitted,
    responseSelections,
    qetaPostId,
    prevQetaPostIds,
  ]);

  return (
    <>
      <Dialog open={open && isDialogVisible} onClose={() => !saving && onClose()}>
      {(saving || loadingPrev) && <Progress />}
      <DialogTitle>{feedbackDialogTitle}</DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Select all that apply</FormLabel>
          <FormGroup className={classes.boxContainer}>
            {feedbackDialogResponses.map(response => (
              <Grid
                container
                key={response.id}
                direction="column"
                spacing={1}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={responseSelections[response.id] ?? false}
                      disabled={saving}
                      name={response.id}
                      onChange={e =>
                        setResponseSelections({
                          ...responseSelections,
                          [e.target.name]: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  }
                  label={response.label}
                />
                <Collapse in={responseSelections[response.id]}>
                  <TextField
                    disabled={saving}
                    className={classes.commentBoxes}
                    multiline
                    minRows={2}
                    fullWidth
                    variant="outlined"
                    inputProps={{ 'aria-label': `${response.label} comments` }}
                    value={comments.responseComments[response.id] || ''}
                    onChange={e =>
                      setComments(prev => ({
                        responseComments: {
                          ...prev.responseComments,
                          [response.id]: e.target.value,
                        },
                        additionalComments: prev.additionalComments,
                      }))
                    }
                  />
                </Collapse>
              </Grid>
            ))}
          </FormGroup>
        </FormControl>
        <FormControl fullWidth>
          <FormLabel component="legend" className={classes.formLabel}>
            Additional comments
          </FormLabel>
          <TextField
            disabled={saving}
            multiline
            minRows={2}
            inputProps={{ 'aria-label': 'Additional comments' }}
            onChange={e =>
              setComments(prev => ({
                responseComments: { ...prev.responseComments },
                additionalComments: e.target.value,
              }))
            }
            variant="outlined"
            value={comments.additionalComments || ''}
          />
        </FormControl>
        <Typography className={classes.contactConsent}>
          May we contact you about your feedback?
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>No</Grid>
            <Grid item>
              <Switch
                checked={consent}
                disabled={saving}
                onChange={e => setConsent(e.target.checked)}
              />
            </Grid>
            <Grid item>Yes</Grid>
          </Grid>
        </Typography>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button color="primary" disabled={saving} onClick={saveResponse}>
          Submit
        </Button>
        <Button color="primary" disabled={saving} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ top: 80 }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
          Feedback saved successfully
        </Alert>
      </Snackbar>
    </>
  );
};